import { useEffect, useRef } from 'react';

/**
 * Layout compartido para las pantallas de autenticación (LoginPage,
 * RegisterPage, EmailVerificationGate). Aporta:
 *  - Fondo animado con partículas doradas conectadas (canvas vanilla)
 *  - Gradiente animado muy sutil
 *  - Tarjeta con glassmorphism y entrada animada
 *  - Pill "Volver a torneospro.com"
 *
 * No introduce dependencias externas: usa requestAnimationFrame y CSS.
 * Pausa la animación cuando la pestaña está oculta para no consumir batería.
 */
export const AuthLayout = ({ children, maxWidth = 460, slideKey = 'default', variant = 'centered' }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    let raf = null;
    let particles = [];
    let dpr = window.devicePixelRatio || 1;
    let width = 0;
    let height = 0;
    let visible = !document.hidden;

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.scale(dpr, dpr);
    };

    const initParticles = () => {
      const target = Math.min(80, Math.floor((width * height) / 18000));
      particles = Array.from({ length: target }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        radius: Math.random() * 1.5 + 0.5,
        alpha: Math.random() * 0.4 + 0.2,
      }));
    };

    const draw = () => {
      if (!visible) {
        raf = requestAnimationFrame(draw);
        return;
      }
      ctx.clearRect(0, 0, width, height);

      // Update positions
      particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;
      });

      // Draw lines between near particles
      const maxDist = 130;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < maxDist) {
            const opacity = (1 - dist / maxDist) * 0.18;
            ctx.strokeStyle = `rgba(240, 216, 120, ${opacity})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      // Draw particles
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(240, 216, 120, ${p.alpha})`;
        ctx.fill();
      });

      raf = requestAnimationFrame(draw);
    };

    const onVisibilityChange = () => { visible = !document.hidden; };

    resize();
    initParticles();
    draw();

    window.addEventListener('resize', () => { resize(); initParticles(); });
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      position: 'relative',
      overflow: 'hidden',
      background: 'radial-gradient(ellipse at top left, #1a2744 0%, #0f1628 50%, #0a0f1c 100%)',
    }}>
      {/* Gradient orbs animados */}
      <div className="auth-orb auth-orb-1" />
      <div className="auth-orb auth-orb-2" />
      <div className="auth-orb auth-orb-3" />

      {/* Canvas de partículas */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />

      {/* Contenido */}
      <div style={{
        position: 'relative',
        zIndex: 2,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'clamp(16px, 4vw, 32px)',
      }}>
        <a
          href="/"
          className="auth-back-pill"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 18px',
            marginBottom: 20,
            color: 'rgba(255,255,255,0.75)',
            textDecoration: 'none',
            fontSize: '0.82rem',
            fontWeight: 500,
            borderRadius: 999,
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            transition: 'all 0.25s ease',
          }}
        >
          <i className="fa-solid fa-arrow-left"></i> Volver a torneospro.com
        </a>

        {variant === 'split' ? (
          <div style={{width: '100%', maxWidth: 1100}}>
            {children}
          </div>
        ) : (
          <div
            key={slideKey}
            className="auth-card-wrapper"
            style={{
              width: '100%',
              maxWidth,
            }}
          >
            {children}
          </div>
        )}
      </div>

      <style>{`
        .auth-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          pointer-events: none;
          z-index: 0;
          opacity: 0.4;
        }
        .auth-orb-1 {
          width: 500px; height: 500px;
          background: radial-gradient(circle, rgba(240,216,120,0.25) 0%, transparent 70%);
          top: -10%; left: -10%;
          animation: float-orb-1 18s ease-in-out infinite;
        }
        .auth-orb-2 {
          width: 600px; height: 600px;
          background: radial-gradient(circle, rgba(59,130,246,0.18) 0%, transparent 70%);
          bottom: -15%; right: -15%;
          animation: float-orb-2 22s ease-in-out infinite;
        }
        .auth-orb-3 {
          width: 350px; height: 350px;
          background: radial-gradient(circle, rgba(236,72,153,0.14) 0%, transparent 70%);
          top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          animation: float-orb-3 25s ease-in-out infinite;
        }

        @keyframes float-orb-1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(60px, 80px) scale(1.1); }
        }
        @keyframes float-orb-2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-80px, -60px) scale(1.15); }
        }
        @keyframes float-orb-3 {
          0%, 100% { transform: translate(-50%, -50%) scale(1); }
          50% { transform: translate(-50%, -50%) scale(1.2); }
        }

        .auth-back-pill:hover {
          background: rgba(240,216,120,0.15) !important;
          border-color: rgba(240,216,120,0.4) !important;
          color: #f0d878 !important;
          transform: translateX(-3px);
        }

        .auth-card-wrapper {
          animation: card-enter 0.9s cubic-bezier(0.16, 1, 0.3, 1) both;
          view-transition-name: auth-card;
        }

        @keyframes card-enter {
          0% {
            opacity: 0;
            transform: translateY(60px) scale(0.92);
            filter: blur(8px);
          }
          60% {
            filter: blur(0);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
            filter: blur(0);
          }
        }

        /* Glassmorphism para el card de auth (override de .login-card) */
        .auth-card-wrapper .login-card {
          background: rgba(22, 33, 62, 0.45) !important;
          backdrop-filter: blur(28px) saturate(200%);
          -webkit-backdrop-filter: blur(28px) saturate(200%);
          border: 1px solid rgba(240, 216, 120, 0.18) !important;
          box-shadow:
            0 25px 50px -12px rgba(0, 0, 0, 0.7),
            0 0 60px -20px rgba(240, 216, 120, 0.25),
            0 0 0 1px rgba(255, 255, 255, 0.04),
            inset 0 1px 0 rgba(255, 255, 255, 0.08) !important;
          border-radius: 20px !important;
          overflow: hidden;
          position: relative;
        }

        /* Brillo dorado animado en el borde superior del card */
        .auth-card-wrapper .login-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 1px;
          background: linear-gradient(90deg,
            transparent 0%,
            rgba(240, 216, 120, 0.6) 50%,
            transparent 100%);
          animation: shine-top 4s ease-in-out infinite;
        }

        @keyframes shine-top {
          0%, 100% { opacity: 0.3; transform: translateX(-30%); }
          50% { opacity: 1; transform: translateX(30%); }
        }

        /* Slide horizontal cuando se cambia entre login y registro
           (controlado por la clase auth-mode-login / auth-mode-register).
           El AuthLayout es el mismo nodo persistente, solo se anima el card. */
        .auth-mode-login .login-card,
        .auth-mode-register .login-card {
          animation: card-swap-in 0.55s cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        @keyframes card-swap-in {
          0% {
            opacity: 0;
            transform: translateX(40px) scale(0.96);
            filter: blur(6px);
          }
          100% {
            opacity: 1;
            transform: translateX(0) scale(1);
            filter: blur(0);
          }
        }

        .auth-card-wrapper .login-header {
          background: linear-gradient(135deg, rgba(26, 39, 68, 0.5), rgba(15, 22, 40, 0.5)) !important;
          border-bottom: 1px solid rgba(240, 216, 120, 0.15) !important;
          position: relative;
        }

        .auth-card-wrapper .login-header::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent, #f0d878, transparent);
          opacity: 0.6;
        }

        .auth-card-wrapper .login-logo img {
          filter: drop-shadow(0 4px 12px rgba(240, 216, 120, 0.3));
          transition: transform 0.3s ease;
        }
        .auth-card-wrapper .login-logo:hover img {
          transform: scale(1.05) rotate(-3deg);
        }

        /* Inputs con focus dorado */
        .auth-card-wrapper .form-input,
        .auth-card-wrapper .form-select {
          background: rgba(255, 255, 255, 0.04) !important;
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
          transition: all 0.2s ease;
        }
        .auth-card-wrapper .form-input:focus,
        .auth-card-wrapper .form-select:focus {
          border-color: rgba(240, 216, 120, 0.5) !important;
          box-shadow: 0 0 0 3px rgba(240, 216, 120, 0.12) !important;
          background: rgba(255, 255, 255, 0.06) !important;
        }

        /* Reduced motion: deshabilitar animaciones para usuarios que lo prefieran */
        @media (prefers-reduced-motion: reduce) {
          .auth-orb,
          .auth-card-wrapper {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AuthLayout;
